import { demoChapters } from "./landing-media.js";
import { keycaps, quickHistoryPanel, stageArt, tileArt } from "./landing-art.js";
import { annualSavingsMonths, landingPricing, lowestPaidPrice, planPrices } from "./landing-pricing.js";

const escapeAttr = (value) => String(value ?? "").replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;");

export function shortcutKeys(platform) {
  return platform === "windows" ? ["Ctrl", "Shift", "V"] : ["⌃", "⇧", "V"];
}

const tools = ["Clipboard History", "Quick History", "Pins", "Collections", "Area capture", "Screen recording", "GIF", "Image Studio", "Blur & pixelate", "Grab text", "Video Studio", "Soundboard", "Prompts", "Keybind Center", "Encrypted sync", "Web History"];

const tiles = [
  ["memory", "wide", "A clipboard with a memory", "Every copy is saved, searchable and pinnable. Yesterday's link is two keystrokes away."],
  ["capture", "", "Capture anything", "Full screen, one window or a pixel-exact area. Record the screen when a still won't do."],
  ["markup", "", "Mark up like a pro", "Arrows, boxes and callouts. Blur and pixelate what nobody else should see."],
  ["ocr", "", "Text out of pixels", "Copy the words inside any screenshot, scan or slide."],
  ["video", "wide", "A video studio that keeps up", "Trim, layer and score your recordings on a real timeline, with sound cues built in."],
  ["prompts", "", "Prompts and snippets", "Save what you type every day. Fill in the blanks as you paste."],
  ["keys", "wide", "Your keys, your way", "Every action has a shortcut, and Keybind Center lets you change any of them."],
  ["sync", "wide", "Private by design", "Local-first. Pro+ sync is end-to-end encrypted, so only your devices can read it."],
];

// Kept word for word from the previous home page: it states what the service can
// and can't see, and routing.test.js holds it to beta-honest wording.
const securityPoints = [
  ["Private content", "Synced clipboard contents and images are encrypted on your device. DittyBag’s cloud receives encrypted data, not the recovery key needed to read it."],
  ["Protected access", "Google sign-in, optional authenticator 2FA, recovery codes, trusted-device approval, and device revocation protect account access."],
  ["Keys stay with you", "The desktop app protects sync credentials in the operating system’s credential store. Web History keeps its key only in temporary tab storage for the unlock time you choose, so refreshes work without making the key permanent."],
  ["Honest boundaries", "The service keeps limited account, device, timing, content-type, and size information needed to route sync and enforce storage limits. Local history is protected by your computer account and full-disk encryption."],
];

function securitySection() {
  return `<section class="lp-section lp-security" id="security" aria-labelledby="security-title">
    <header class="lp-head"><p class="lp-eyebrow">SECURE BY DESIGN</p><h2 id="security-title">Your content is encrypted before it leaves your device.</h2><p><span class="lp-security-badge">Controls documented</span> <a href="/secure">Read how DittyBag protects your data</a></p></header>
    <div class="lp-security-grid">${securityPoints.map(([title, text], index) => `<article data-reveal><span>${String(index + 1).padStart(2, "0")}</span><h3>${title}</h3><p>${text}</p></article>`).join("")}</div>
    <aside class="lp-security-note"><strong>Keep your recovery key private.</strong> Anyone who has it and access to your encrypted history may be able to read that history. Revoking a computer blocks future sync access, but cannot erase copies already stored on that computer.</aside>
  </section>`;
}

const faqs = [
  ["Is Pro a subscription?", "No. Pro is a one-time purchase. Pro+ is the subscription, because it runs encrypted sync and Web History for you.", "prices"],
  ["Does my clipboard get uploaded?", "Only if you turn on Pro+ sync. DittyBag works fully offline, and synced clips are encrypted on your device before they leave it."],
  ["What about passwords and private apps?", "Pause History any time with privacy mode, and exclude apps such as your password manager so they're never recorded."],
  ["Which computers does it run on?", "Apple silicon Macs and Windows PCs. Pro covers up to three of your computers."],
  ["Can I try it first?", "Yes. The free version has no time limit, and you can upgrade from inside the app when you're ready."],
];

function planCta(plan) {
  if (plan.id === "free") return `<a class="lp-btn lp-btn--ghost" href="#download">Download free</a>`;
  if (landingPricing.checkoutOpen) {
    return `<a class="lp-btn ${plan.featured ? "lp-btn--volt" : "lp-btn--ghost"}" href="${escapeAttr(`${landingPricing.checkoutPath}?plan=${plan.id}`)}" data-plan-cta="${plan.id}">Get ${plan.name.replace("DittyBag ", "")}</a>`;
  }
  return `<a class="lp-btn ${plan.featured ? "lp-btn--volt" : "lp-btn--ghost"}" href="#download" data-plan-cta="${plan.id}">Download the beta</a>`;
}

function priceBlock(plan, showPrices) {
  const prices = planPrices(plan);
  if (!prices.length) return `<div class="lp-price"><strong>Free</strong><span>forever</span></div>`;
  if (!showPrices) return `<div class="lp-price lp-price--soon"><strong>Soon</strong><span>price at launch</span></div>`;
  if (prices.length === 1) {
    const [row] = prices;
    return `<div class="lp-price"><strong>${row.price}</strong><span>${row.period === "once" ? "one time" : row.period}</span></div>`;
  }
  const savings = annualSavingsMonths(plan);
  return `<div class="lp-price" data-billing-prices>${prices.map((row) =>
    `<span class="lp-price-option" data-interval="${row.interval}"${row.interval === "year" ? "" : " hidden"}><strong>${row.price}</strong><span>${row.period}</span></span>`).join("")}</div>
    <div class="lp-billing" role="group" aria-label="Billing period"><button type="button" data-billing="month" aria-pressed="false">Monthly</button><button type="button" data-billing="year" aria-pressed="true">Yearly${savings ? ` <em>${savings} months free</em>` : ""}</button></div>`;
}

function pricingCards(showPrices) {
  return landingPricing.plans.map((plan) => `<article class="lp-plan${plan.featured ? " is-featured" : ""}" data-plan="${plan.id}">
    ${plan.badge ? `<span class="lp-plan-badge">${plan.badge}</span>` : ""}
    <h3>${plan.name}</h3><p class="lp-plan-tag">${plan.tagline}</p>
    ${priceBlock(plan, showPrices)}
    <ul>${plan.features.map((feature) => `<li>${feature}</li>`).join("")}</ul>
    ${planCta(plan)}
  </article>`).join("");
}

export function demoStage(chapter, media) {
  const real = media?.chapters?.[chapter.id];
  if (real?.video) {
    const sources = `${real.webm ? `<source src="${real.webm}" type="video/webm">` : ""}<source src="${real.video}" type="video/mp4">`;
    // Native controls are the no-JS fallback; landing-motion.js swaps in the page's own player.
    return `<video class="lp-stage-video" muted playsinline controls preload="metadata"${real.poster ? ` poster="${real.poster}"` : ""} aria-label="${escapeAttr(chapter.title)}">${sources}</video>`;
  }
  if (real?.poster) return `<img class="lp-stage-video" src="${real.poster}" alt="${escapeAttr(chapter.title)}" loading="lazy">`;
  return stageArt[chapter.id]?.() ?? "";
}

const playerIcons = {
  back: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3M4.5 4.5v4.2h4.2"/></svg>`,
  play: `<svg class="lp-icon-play" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l10.5-6.5z"/></svg>`,
  pause: `<svg class="lp-icon-pause" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13M16 5.5v13"/></svg>`,
};

// One player for every chapter: real recordings and illustrated scenes share
// the same timeline, pause/play and rewind (wired up in landing-motion.js).
function playerControls() {
  return `<div class="lp-player" data-player hidden>
    <button type="button" class="lp-player-btn" data-player-back aria-label="Back 5 seconds" title="Back 5 seconds">${playerIcons.back}</button>
    <button type="button" class="lp-player-btn lp-player-toggle" data-player-toggle aria-label="Pause" title="Pause">${playerIcons.play}${playerIcons.pause}</button>
    <input class="lp-player-seek" type="range" min="0" max="1000" step="1" value="0" data-player-seek aria-label="Demo timeline">
    <span class="lp-player-time" data-player-time>0:00 / 0:00</span>
  </div>`;
}

function demoSection(media) {
  const list = demoChapters.map((chapter, index) => `<li><button type="button" class="lp-chapter${index === 0 ? " is-active" : ""}" data-chapter="${chapter.id}" aria-pressed="${index === 0}">
      <span class="lp-chapter-kicker">${String(index + 1).padStart(2, "0")} · ${chapter.kicker}</span>
      <strong>${chapter.title}</strong><span class="lp-chapter-blurb">${chapter.blurb}</span>
      <span class="lp-chapter-progress" aria-hidden="true"><i></i></span></button></li>`).join("");
  const stages = demoChapters.map((chapter, index) =>
    `<div class="lp-stage-slide${index === 0 ? " is-active" : ""}" data-stage="${chapter.id}"${index === 0 ? "" : " hidden"}>${demoStage(chapter, media)}</div>`).join("");
  return `<section class="lp-section lp-demos" id="demos" aria-labelledby="demos-title">
    <header class="lp-head"><p class="lp-eyebrow">WATCH IT WORK</p><h2 id="demos-title">Seven moves you'll make every day.</h2></header>
    <div class="lp-demo-grid"><ol class="lp-chapters">${list}</ol><div class="lp-stage-col"><div class="lp-stage" data-demo-stage>${stages}<span class="lp-stage-glow" aria-hidden="true"></span></div>${playerControls()}</div></div>
  </section>`;
}

function screenshotStrip(media) {
  if (!media?.screenshots?.length) return "";
  return `<section class="lp-section lp-shots" aria-label="Screenshots"><div class="lp-shot-track">${media.screenshots.map((shot) =>
    `<figure><img src="${shot.src}" alt="${escapeAttr(shot.caption)}" width="${shot.width}" height="${shot.height}" loading="lazy"><figcaption>${escapeAttr(shot.caption)}</figcaption></figure>`).join("")}</div></section>`;
}

// Today's DittyBag icon beside the wordmark. The logo work will hand over
// finished lockups; until then this is the one place the page draws the brand.
function brandLockup(icon, size) {
  return `<span class="lp-lockup lp-lockup--${size}"><img class="lp-lockup-icon" src="${escapeAttr(icon)}" alt="" width="512" height="512"><span class="lp-wordmark">DittyBag</span></span>`;
}

export function landingNavLinks() {
  return `<a href="#features">Features</a><a href="#demos">Demos</a><a href="#pricing">Pricing</a><a href="#security">Security</a>`;
}

export function landingMarkup({ errorNotice = "", version = "", platform = "unknown", media = null, downloadActions = "", brandIcon = "/brand/dittybag-icon.png", showPrices = landingPricing.showPrices } = {}) {
  const keys = shortcutKeys(platform);
  const pro = showPrices ? lowestPaidPrice() : null;
  const heroCtas = showPrices
    ? `<a class="lp-btn lp-btn--volt lp-btn--big" href="#pricing" data-cta="hero-pro">${pro ? `Get Pro · ${pro.price}` : "Get Pro"}</a><a class="lp-btn lp-btn--ghost lp-btn--big" href="#download">Download free</a>`
    : `<a class="lp-btn lp-btn--volt lp-btn--big" href="#download" data-cta="hero-download">Download the beta</a><a class="lp-btn lp-btn--ghost lp-btn--big" href="#pricing">See the plans</a>`;
  const pricingHead = showPrices
    ? `<h2 id="pricing-title">Pay once. Keep it.</h2><p>Start free. Upgrade when you want the whole bag.</p>`
    : `<h2 id="pricing-title">Start free. Go Pro at launch.</h2><p>Prices are announced at launch. The beta is free until then.</p>`;
  const fine = landingPricing.checkoutOpen ? "" : showPrices
    ? "Checkout opens at launch. The beta download is free in the meantime."
    : "Pro and Pro+ open at launch. Download the free beta today.";
  const tileMarkup = tiles.map(([id, size, title, text]) =>
    `<article class="lp-tile${size ? ` lp-tile--${size}` : ""}" data-reveal>${tileArt[id]}<h3>${title}</h3><p>${text}</p></article>`).join("");
  const ticker = tools.map((tool) => `<span>${tool}</span>`).join("");
  return `${errorNotice}
  <section class="lp-hero" aria-labelledby="hero-title">
    <div class="lp-hero-copy">
      ${brandLockup(brandIcon, "hero")}
      <p class="lp-pill"><span class="lp-led"></span>DittyBag ${escapeAttr(version)} for Mac and Windows</p>
      <h1 id="hero-title">Copy once.<br><em>Find it <span class="lp-spark-word">forever</span>.</em></h1>
      <p class="lp-lede">DittyBag remembers everything you copy, from text and links to screenshots and screen recordings, and hands it back with one keystroke. Then it gives you the tools to mark it up, cut it together and paste it anywhere.</p>
      <div class="lp-cta-row">${heroCtas}</div>
      <ul class="lp-trust"><li>${showPrices ? "One-time purchase" : "Free beta"}</li><li>Works offline</li><li>Encrypted sync on Pro+</li></ul>
    </div>
    <div class="lp-hero-visual">
      <div class="lp-press">Press ${keycaps(keys, "lp-keys--hero")}</div>
      ${quickHistoryPanel(keys)}
      <span class="lp-float lp-float-a" aria-hidden="true">Copied · Figma</span>
      <span class="lp-float lp-float-b" aria-hidden="true">Screenshot · 1440 × 900</span>
      <span class="lp-float lp-float-c" aria-hidden="true">Link · github.com</span>
    </div>
  </section>
  <div class="lp-ticker" aria-hidden="true"><div class="lp-ticker-track">${ticker}${ticker}</div></div>
  <section class="lp-section lp-flow" aria-labelledby="flow-title">
    <header class="lp-head"><p class="lp-eyebrow">HOW IT FEELS</p><h2 id="flow-title">Three moves. Zero hunting.</h2></header>
    <ol class="lp-steps">
      <li data-reveal><span class="lp-step-num">1</span>${keycaps(platform === "windows" ? ["Ctrl", "C"] : ["⌘", "C"])}<h3>Copy like always</h3><p>DittyBag quietly keeps it, with where it came from.</p></li>
      <li data-reveal><span class="lp-step-num">2</span>${keycaps(keys)}<h3>Summon your bag</h3><p>Quick History opens right where you're working. Type two letters.</p></li>
      <li data-reveal><span class="lp-step-num">3</span>${keycaps(["↵"])}<h3>Paste and go</h3><p>It lands in the app you were in. You never left.</p></li>
    </ol>
  </section>
  <section class="lp-section lp-features" id="features" aria-labelledby="features-title">
    <header class="lp-head"><p class="lp-eyebrow">TOOLS IN THE BAG</p><h2 id="features-title">Eight tools. One shortcut away.</h2><p>Everything you reach for between copy and paste, built into one fast native app.</p></header>
    <div class="lp-bento">${tileMarkup}</div>
  </section>
  ${demoSection(media)}
  ${screenshotStrip(media)}
  <section class="lp-section lp-pricing" id="pricing" aria-labelledby="pricing-title">
    <header class="lp-head"><p class="lp-eyebrow">PRICING</p>${pricingHead}</header>
    <div class="lp-plans">${pricingCards(showPrices)}</div>
    ${fine ? `<p class="lp-fine">${fine}</p>` : ""}
  </section>
  ${securitySection()}
  <section class="lp-section lp-faq" aria-labelledby="faq-title">
    <header class="lp-head"><p class="lp-eyebrow">QUESTIONS</p><h2 id="faq-title">Good to know.</h2></header>
    <div class="lp-faq-list">${faqs.filter(([, , needs]) => showPrices || needs !== "prices").map(([q, a]) => `<details><summary>${q}</summary><p>${a}</p></details>`).join("")}</div>
  </section>
  <section class="lp-final" id="download" aria-labelledby="final-title">
    ${brandLockup(brandIcon, "final")}
    <h2 id="final-title">Put your clipboard<br><em>in the bag.</em></h2>
    <p>Free to start. Native on Mac and Windows. No account needed.</p>
    ${downloadActions}
  </section>`;
}
